import axios from 'axios';
import type {
  MenuTreeNode,
  MenuListItem,
  MenuDetailResponse,
  CreateMenuRequest,
  UpdateMenuRequest,
  UpdateMenuOrderRequest,
  MyMenuItem,
  PageResponse,
  MenuSearchCondition,
  PermissionItem,
  RoleListItemForPreview,
  RoleDetailForPreview,
} from '../types/menu';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const menuApi = {
  /** GET /api/eraf/menus — 전체 트리 (관리자용, depth ≤ 3) */
  getTree: (): Promise<MenuTreeNode[]> =>
    apiClient
      .get<unknown, { data: MenuTreeNode[] }>('/menus')
      .then((res) => res.data),

  /** GET /api/eraf/menus/flat — flat 검색 (관리자용 페이징) */
  getFlat: (params?: MenuSearchCondition): Promise<PageResponse<MenuListItem>> => {
    const sp = new URLSearchParams();
    if (params?.keyword) sp.set('keyword', params.keyword);
    if (params?.menuType) sp.set('menuType', params.menuType);
    if (params?.visible !== undefined) sp.set('visible', String(params.visible));
    if (params?.page !== undefined) sp.set('page', String(params.page));
    if (params?.size !== undefined) sp.set('size', String(params.size));
    if (params?.sort) sp.set('sort', params.sort);
    const qs = sp.toString();
    return apiClient
      .get<unknown, { data: PageResponse<MenuListItem> }>(`/menus/flat${qs ? `?${qs}` : ''}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/menus/{id} — 메뉴 단건 상세 */
  getById: (id: number): Promise<MenuDetailResponse> =>
    apiClient
      .get<unknown, { data: MenuDetailResponse }>(`/menus/${id}`)
      .then((res) => res.data),

  /** POST /api/eraf/menus — 메뉴 생성 */
  create: (body: CreateMenuRequest): Promise<MenuDetailResponse> =>
    apiClient
      .post<unknown, { data: MenuDetailResponse }>('/menus', body)
      .then((res) => res.data),

  /** PATCH /api/eraf/menus/{id} — 메뉴 부분 수정 */
  update: (id: number, body: UpdateMenuRequest): Promise<MenuDetailResponse> =>
    apiClient
      .patch<unknown, { data: MenuDetailResponse }>(`/menus/${id}`, body)
      .then((res) => res.data),

  /** DELETE /api/eraf/menus/{id} — 메뉴 소프트 삭제 */
  delete: (id: number): Promise<void> =>
    apiClient.delete(`/menus/${id}`).then(() => undefined),

  /** PATCH /api/eraf/menus/order — 벌크 순서 변경 */
  reorder: (body: UpdateMenuOrderRequest): Promise<MenuListItem[]> =>
    apiClient
      .patch<unknown, { data: MenuListItem[] }>('/menus/order', body)
      .then((res) => res.data),

  /** GET /api/eraf/menus/me — 내 역할 기반 가시 메뉴 트리 */
  getMyMenus: (): Promise<MyMenuItem[]> =>
    apiClient
      .get<unknown, { data: MyMenuItem[] }>('/menus/me')
      .then((res) => res.data),

  /**
   * GET /api/eraf/permissions — 권한 카탈로그 전체 목록.
   * #471: 필요 권한 드롭다운 옵션 source.
   * role-management 내부를 import 하지 않고 menuApi 의 동일한 axios client 재사용.
   */
  getPermissions: (): Promise<PermissionItem[]> =>
    apiClient
      .get<unknown, { data: PermissionItem[] }>('/permissions')
      .then((res) => res.data),

  /**
   * GET /api/eraf/roles?size=200 — 전체 역할 목록 (preview simulator 용).
   * #472: 역할 선택자를 동적으로 바꾸기 위해 DB 실제 역할 목록을 조회.
   * role-management 내부를 import 하지 않고 동일한 axios client 재사용.
   */
  getRoles: (): Promise<RoleListItemForPreview[]> =>
    apiClient
      .get<unknown, { data: { content: RoleListItemForPreview[] } }>('/roles?size=200&sort=priority,desc')
      .then((res) => res.data.content),

  /**
   * GET /api/eraf/roles/{id} — 역할 상세 (permissions 포함).
   * #472: flat predicate 의 permission 분기 평가에 사용.
   */
  getRoleDetail: (id: number): Promise<RoleDetailForPreview> =>
    apiClient
      .get<unknown, { data: RoleDetailForPreview }>(`/roles/${id}`)
      .then((res) => res.data),
};
